import {
  addCompanyUser,
  deleteCompanyUser,
  getCompanyUsers,
  resetCompanyUserPassword,
  updateCompanyUser,
} from "../actions/company.user.action";
import TableList from "../components/TableList";
import { CompanyUser, CompanyUserType } from "../models/company.user.model";
import { useRef } from "react";
import CompanyUserForm from "./CompanyUserForm";
import ResetPasswordForm from "./ResetPasswordForm";
import { filterOnKeys, titleStr } from "../lib/helper";
import { FilterGroup, FilterType } from "../components/TableFilter";
import { FormRefEvent, HeadCell } from "../models/base.model";
import { Chip } from "@mui/material";
import { CheckCircleOutline, HighlightOff, LockReset } from "@mui/icons-material";
import { green, red } from "@mui/material/colors";
import { useConfig } from "./ConfigContext";

const user_type_color_map: any = {
  root: "primary",
  admin: "success",
  viewer: "info",
};

export default function CompanyUsers() {
  const { localized, loggedUser } = useConfig();
  const headCells: readonly HeadCell[] = [
    {
      id: "name",
      label: localized("Name"),
    },
    {
      id: "email",
      label: localized("Email"),
      render: (v, r: CompanyUser) => (
        <span>{~(v || "").indexOf(r.companyId) ? "" : v}</span>
      ),
    },
    {
      id: "type",
      label: localized("User Type"),
      render: (v) => (
        <Chip color={user_type_color_map[v]} label={titleStr(v)} />
      ),
    },
    {
      id: "enabled",
      label: localized("Enabled"),
      render: (value) =>
        value ? (
          <CheckCircleOutline sx={{ color: green[500] }} />
        ) : (
          <HighlightOff sx={{ color: red[500] }} />
        ),
    },
  ];

  const filters: FilterGroup<CompanyUser>[] = [
    {
      title: localized("Filter By Date"),
      items: [
        {
          name: "createdAt",
          type: FilterType.date_range,
        },
        {
          name: "updatedAt",
          type: FilterType.date_range,
        },
      ],
    },
    {
      title: localized("Filter By Data"),
      items: [
        {
          name: "type",
          type: FilterType.selection,
        },

        {
          name: "enabled",
          type: FilterType.selection,
          options: [
            { value: true, label: localized("Enabled") },
            { value: false, label: localized("Disabled") },
          ],
        },
      ],
    },
  ];

  const formRef = useRef<FormRefEvent<CompanyUser>>(null);
  const resetPasswordRef = useRef<FormRefEvent<{ password: string }>>(null);

  const onAddOrUpdate = async (param?: CompanyUser) => {
    if (param) {
      (param as any).usernameOrEmail = param.email;
    }
    return window.webApp.setTab<CompanyUser>({
      title: `${param ? "Update" : "New"} Company User`,
      element: <CompanyUserForm ref={formRef} defaultValues={param} />,
      onOk: async () => {
        if (!formRef.current) {
          throw new Error("Form creation error, please refresh or contact us!");
        }
        const value = await formRef.current.onSubmit();
        if (value.type === CompanyUserType.admin && loggedUser) {
          value.hierarchies = [loggedUser.companyId];
        }
        const res = param
          ? await updateCompanyUser({ ...param, ...value })
          : await addCompanyUser(value);
        return res;
      },
      show: true,
    });
  };

  const onResetPassword = (user: CompanyUser) => {
    return window.webApp.toggleDialog({
      open: true,
      title: `Reset Password — ${user.name}`,
      element: <ResetPasswordForm ref={resetPasswordRef} />,
      onOk: async () => {
        if (!resetPasswordRef.current) {
          throw new Error("Form creation error, please refresh or contact us!");
        }
        const { password } = await resetPasswordRef.current.onSubmit();
        await resetCompanyUserPassword({ username: user.username, password, companyId: user.companyId });
        navigator.clipboard.writeText(password);
        window.webApp.showMessage("success", "Password reset successfully and copied to clipboard");
      },
    });
  };

  const onDelete = (param: CompanyUser) => {
    return deleteCompanyUser(param.username, param.companyId);
  };

  const onSearch = async (value: string, original: CompanyUser[]) => {
    if (original) {
      return {
        data: original.filter((a) => filterOnKeys(a, ["name", "email"], value)),
      };
    } else {
      return { data: [] };
    }
  };

  return (
    <TableList<CompanyUser>
      headCells={headCells}
      onAddBtnText={localized("New Company User")}
      uid="username"
      onAdd={onAddOrUpdate}
      onSearch={onSearch}
      searchText="Search by name, email"
      onEdit={onAddOrUpdate}
      onDelete={onDelete}
      onLoad={getCompanyUsers}
      filters={filters}
      actions={(row) => [
        {
          label: "Reset Password",
          icon: <LockReset />,
          onClick: () => { onResetPassword(row); },
        },
      ]}
    />
  );
}
